import React, { useState, useEffect } from "react";
import {
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Typography,
  Grid,
  FormControl,
  Box
} from "@mui/material";
import { AnanseApiService } from "app/services/Ananse/AnanseApiService";
import { Spinner } from "reactstrap";
import { useTranslation } from "_metronic/i18n/language";
import { useRTL } from "themes/RTLProvider/RTLProvider";
import { useThemeApp } from "_metronic/materialUIThemeProvider/ThemeProvider";

interface IConvertData {
  id: number;
  value: number;
  label: string;
}

interface Props {
  endPoint: string;
  label: string;
  handleChange?: any;
  convertObject?: any;
  value?: any;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  multiple?: boolean;
  disabled?: boolean;
}

const SimpleSelectPage = ({
  convertObject,
  endPoint,
  handleChange,
  label,
  value,
  setLoading,
  multiple,
  disabled
}: Props) => {
  const translate = useTranslation();
  const isRtl = useRTL();
  const textAlignIfRtl = isRtl ? "right" : "left";
  const { isDarkMode } = useThemeApp();
  const [data, setData] = React.useState<Array<IConvertData>>([]);
  const [loadingData, setLoadingData] = useState(false);

  const api = new AnanseApiService();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoadingData(true);
    if (setLoading) {
      setLoading(true);
    }
    try {
      const searchedList: Array<IConvertData> = [];
      const response = await api.makeHttpRequest({
        method: "GET",
        url: endPoint
      });

      // Verifica se a resposta é um array direto ou se tem propriedade data
      const dataArray = Array.isArray(response) ? response : response.data || [];

      dataArray.forEach((item: any) => {
        const converted = convertObject ? convertObject(item) : {
          id: item.id,
          value: item.id,
          label: item.name || item.label || item.title || `Item ${item.id}`
        };
        // Só adiciona se o objeto convertido não for null e tiver label
        if (converted && converted.label && searchedList.indexOf(converted) === -1) {
          searchedList.push(converted);
        }
      });

      setData(searchedList.sort((a, b) => a.label.localeCompare(b.label)));
    } catch (error) {
      console.error("Error fetching data: ", error);
    } finally {
      setLoadingData(false);
      if (setLoading) {
        setLoading(false);
      }
    }
  };

  const handlecharge = (event: any) => {
    if (multiple) {
      const {
        target: { value }
      } = event;
      handleChange(value);
      return;
    }
    const filter = data.find(item => item.id === event.target.value);
    handleChange(filter);
  };

  return (
    <FormControl fullWidth margin="normal">
      <InputLabel
        id="simple-select-helper-label"
        sx={{
          color: isDarkMode ? "#B0B0B0" : "#666666",
          '&.Mui-focused': {
            color: isDarkMode ? "#90caf9" : "#1A73E8",
          },
        }}
      >
        {label}
      </InputLabel>

      <Select
        labelId="simple-select-label"
        id="simple-select"
        label={label}
        value={value ?? 0}
        onChange={handlecharge}
        multiple={multiple}
        disabled={disabled}
        fullWidth
        sx={{
          backgroundColor: isDarkMode ? "#1E1E1E" : "#FFFFFF",
          color: isDarkMode ? "#E0E0E0" : "#333333",
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: isDarkMode ? "#333333" : "#DDDDDD",
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: isDarkMode ? "#555555" : "#999999",
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: isDarkMode ? "#90caf9" : "#1A73E8",
          },
          '& .MuiSelect-icon': {
            color: isDarkMode ? "#B0B0B0" : "#666666",
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: isDarkMode ? "#1E1E1E" : "#FFFFFF",
              border: `1px solid ${isDarkMode ? "#333333" : "#DDDDDD"}`,
              maxHeight: 300,
            },
          },
        }}
      >
        <MenuItem
          value={0}
          disabled={value}
          sx={{
            color: isDarkMode ? "#B0B0B0" : "#666666",
            backgroundColor: isDarkMode ? "#1E1E1E" : "#FFFFFF",
            '&:hover': {
              backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
            },
          }}
        >
          {loadingData ? (
            <Grid
              container
              alignItems="center"
              spacing={2}
            >
              <Grid item>
                <Spinner size="sm"/>
              </Grid>
              <Grid item>
                <Typography
                  sx={{
                    color: isDarkMode ? "#B0B0B0" : "#666666",
                  }}
                >
                  {translate("screens.loading.title")}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Box
              sx={{
                textAlign: textAlignIfRtl,
                width: "100%",
              }}
            >
              {label}
            </Box>
          )}
        </MenuItem>
        {data.map((item: any) => (
          <MenuItem
            key={item.id}
            value={item.id}
            selected={item.id === value}
            disabled={disabled}
            sx={{
              backgroundColor: item.id === value
                ? (isDarkMode ? "rgba(144, 202, 249, 0.2)" : "rgba(26, 115, 232, 0.1)")
                : (isDarkMode ? "#1E1E1E" : "#FFFFFF"),
              color: isDarkMode ? "#E0E0E0" : "#333333",
              '&:hover': {
                backgroundColor: isDarkMode
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.05)",
              },
              '&.Mui-selected': {
                backgroundColor: isDarkMode
                  ? "rgba(144, 202, 249, 0.2)"
                  : "rgba(26, 115, 232, 0.1)",
                '&:hover': {
                  backgroundColor: isDarkMode
                    ? "rgba(144, 202, 249, 0.3)"
                    : "rgba(26, 115, 232, 0.2)",
                },
              },
            }}
          >
            {multiple && (
              <Checkbox
                color="primary"
                checked={value.indexOf(item.id) > -1}
                disabled={disabled}
                sx={{
                  color: isDarkMode ? "#B0B0B0" : "#666666",
                  '&.Mui-checked': {
                    color: isDarkMode ? "#90caf9" : "#1A73E8",
                  },
                }}
              />
            )}
            <Box
              sx={{
                textAlign: textAlignIfRtl,
                width: "100%",
                marginLeft: multiple ? 1 : 0,
              }}
            >
              {item.label}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SimpleSelectPage;
